import { debounce, logger, stringUtils } from "cayo.ui";
import { showError } from "@cayo/shared/components/App/utils/showError";
import tokenStorage from "@cayo/shared/settings/tokenStorage";
import { ajaxUtils } from "@cayo/shared/utils/ajax";
import { deleteCookie, setCookie } from "@cayo/shared/utils/cookie";

const TOKEN_COOKIE_EXPIRATION_DAYS = 2.31481e-5;
const DELETE_COOKIE_DEBOUNCE_MS = 2000;

function getFilenameFromDisposition(disposition: string | null): string | null {
  if (!disposition) {
    return null;
  }

  const utf8Match = disposition.match(/filename\*\s*=\s*UTF-8''([^;\n]+)/i);
  if (utf8Match) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch {
      return utf8Match[1];
    }
  }

  const fallbackMatch = disposition.match(/filename\s*=\s*"?([^"]+)"?/i);
  if (fallbackMatch) {
    return fallbackMatch[1];
  }

  return null;
}

const log = logger.getLogger("download-utils");

export const matchFileNameRegex = /filename=([^;]+)/;

const downloadStream = async (response: Blob | Response, headers: any) => {
  const blob = response instanceof Blob ? response : await response.blob();

  const contentDisposition = headers["content-disposition"] as string;
  let fileName: string = "response.txt";
  if (contentDisposition) {
    const match = contentDisposition.match(matchFileNameRegex);
    if (match) {
      fileName = stringUtils.trim(match[1], '"');
    }
  }

  // Create an <a> element to simulate the download
  const link = document.createElement("a");
  link.href = window.URL.createObjectURL(blob);
  link.download = fileName; // Set the filename

  // Simulate a click on the link to trigger the download
  link.click();

  URL.revokeObjectURL(link.href);

  //document.body.removeChild(link);
  log.debug(`blod downloaded, fileName: ${fileName}`);
};

const downloadODataFile = (href: string) => {
  withTemporaryToken(() => {
    const a = document.createElement("a");
    a.setAttribute("download", "");
    a.href = href;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  });
};

export function withTemporaryToken(action: () => void) {
  const token = tokenStorage.accessToken;

  if (token) {
    setCookie("token", token, TOKEN_COOKIE_EXPIRATION_DAYS, ["SameSite=Strict"]);
  }

  try {
    action();
  } finally {
    debounce(() => deleteCookie("token"), DELETE_COOKIE_DEBOUNCE_MS)();
  }
}

const downloadODataFiles = async (hrefs: string[]) => {
  const a = document.createElement("a");
  a.setAttribute("download", "");
  a.style.display = "none";
  document.body.appendChild(a);

  const token = tokenStorage.accessToken;
  if (token) {
    setCookie("token", token, TOKEN_COOKIE_EXPIRATION_DAYS, ["SameSite=Strict"]);
  }

  for (const href of hrefs) {
    try {
      const res = await fetch(href);
      if (!res.ok) {
        const error = await ajaxUtils.getError(res);
        showError(`Unable to download: ${error?.error_description}`);
        continue;
      }

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);

      a.href = url;

      const disposition = res.headers.get("Content-Disposition");
      const filename = getFilenameFromDisposition(disposition) ?? "download";

      if (filename) {
        a.download = filename;
      }

      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      const error = await ajaxUtils.getError(err);
      showError(`Unable to download: ${error?.error_description}`);
    }
  }

  document.body.removeChild(a);
  debounce(() => deleteCookie("token"), DELETE_COOKIE_DEBOUNCE_MS)();
};

const downloadUtils = {
  downloadODataFile,
  downloadODataFiles,
  downloadStream,
};

export default downloadUtils;
